import { useState, type ChangeEventHandler } from "react";
import { FieldShell } from "./FieldShell";
import { fieldIds, inputClass, resolveFieldState } from "./fieldStyles";

interface NumberInputProps {
  id: string;
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur?: () => void;
  error?: string | null;
  hint?: string;
  required?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  /** Allow decimals (multipliers). Default: whole numbers only. */
  allowDecimal?: boolean;
  isOverridden?: boolean;
  size?: "default" | "sm";
  hideLabel?: boolean;
  containerClassName?: string;
  children?: never;
}

function parse(raw: string, allowDecimal: boolean): number | null {
  const cleaned = allowDecimal ? raw.replace(/[^\d.-]/g, "") : raw.replace(/[^\d-]/g, "");
  if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Text input with numeric keyboard; the parent receives a number or null, never a string. */
export function NumberInput({ id, label, value, onChange, onBlur, error, hint, required, readOnly, placeholder, min, max, step, allowDecimal = false, isOverridden, size = "default", hideLabel, containerClassName }: NumberInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const ids = fieldIds(id, error, hint);
  const state = resolveFieldState({ error, readOnly, isOverridden });
  const display = draft ?? (value === null ? "" : String(value));

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const raw = event.currentTarget.value;
    setDraft(raw);
    onChange(parse(raw, allowDecimal));
  };

  const handleBlur = () => {
    setDraft(null);
    onBlur?.();
  };

  return (
    <FieldShell id={id} label={label} required={required} hint={hint} hintId={ids.hintId} error={error} errorId={ids.errorId} containerClassName={containerClassName} hideLabel={hideLabel}>
      <input
        id={id}
        type="text"
        inputMode={allowDecimal ? "decimal" : "numeric"}
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        readOnly={readOnly}
        min={min}
        max={max}
        step={step}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={ids.describedBy}
        className={`${inputClass(state, size)} text-right tabular-nums`}
      />
    </FieldShell>
  );
}
